import React, { useState, useEffect } from 'react';
import { LifeBuoy, Plus, MessageSquare, Clock, CheckCircle2, AlertCircle, Send, X, ChevronRight } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import PageHeader from '../../components/layout/PageHeader';
import { AZURE } from '../../services/api';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const SUPPORT_LABELS = {
  pt: {
    statusMap: {
      open: 'Aberto', in_progress: 'Em Andamento', resolved: 'Resolvido', closed: 'Fechado',
    },
    categories: [
      { value: 'general', label: 'Geral' }, { value: 'billing', label: 'Financeiro' },
      { value: 'technical', label: 'Técnico' }, { value: 'feature_request', label: 'Sugestão' },
      { value: 'bug', label: 'Bug' },
    ],
    priorities: [
      { value: 'low', label: 'Baixa' }, { value: 'medium', label: 'Média' },
      { value: 'high', label: 'Alta' }, { value: 'critical', label: 'Crítica' },
    ],
    modalTitle: 'Novo Chamado', subject: 'Assunto', subjectPlaceholder: 'Descreva brevemente o problema...',
    category: 'Categoria', priority: 'Prioridade',
    description: 'Descrição', descriptionPlaceholder: 'Detalhe o problema, passos para reproduzir, etc.',
    cancel: 'Cancelar', creating: 'Criando...', createBtn: 'Criar Chamado',
    originalDesc: 'Descrição original', noMessages: 'Nenhuma mensagem ainda.',
    you: 'Você', support: 'Suporte', replyPlaceholder: 'Escreva uma mensagem...',
    pageTitle: 'Suporte', pageSubtitle: 'Central de chamados e atendimento',
    newTicketBtn: 'Novo Chamado',
    statsTotal: 'Total', statsOpen: 'Abertos', statsResolved: 'Resolvidos',
    filterAll: 'Todos', filterOpen: 'Abertos', filterResolved: 'Resolvidos',
    noTickets: 'Nenhum chamado encontrado.', firstTicket: 'Abrir primeiro chamado',
    noClub: 'Nenhum clube vinculado. Complete o onboarding primeiro.',
  },
  en: {
    statusMap: {
      open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed',
    },
    categories: [
      { value: 'general', label: 'General' }, { value: 'billing', label: 'Billing' },
      { value: 'technical', label: 'Technical' }, { value: 'feature_request', label: 'Feature Request' },
      { value: 'bug', label: 'Bug' },
    ],
    priorities: [
      { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' },
    ],
    modalTitle: 'New Ticket', subject: 'Subject', subjectPlaceholder: 'Briefly describe the issue...',
    category: 'Category', priority: 'Priority',
    description: 'Description', descriptionPlaceholder: 'Detail the problem, steps to reproduce, etc.',
    cancel: 'Cancel', creating: 'Creating...', createBtn: 'Create Ticket',
    originalDesc: 'Original description', noMessages: 'No messages yet.',
    you: 'You', support: 'Support', replyPlaceholder: 'Write a message...',
    pageTitle: 'Support', pageSubtitle: 'Help desk and ticket management',
    newTicketBtn: 'New Ticket',
    statsTotal: 'Total', statsOpen: 'Open', statsResolved: 'Resolved',
    filterAll: 'All', filterOpen: 'Open', filterResolved: 'Resolved',
    noTickets: 'No tickets found.', firstTicket: 'Open first ticket',
    noClub: 'No club linked. Complete onboarding first.',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PriorityDot = ({ priority }) => {
  const colors = { critical: '#dc2626', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };
  return <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[priority] || colors.medium }} />;
};

const StatusBadge = ({ status, t }) => {
  const icons = { open: AlertCircle, in_progress: Clock, resolved: CheckCircle2, closed: CheckCircle2 };
  const bgs = { open: '#dbeafe', in_progress: '#fef3c7', resolved: '#d1fae5', closed: '#f3f4f6' };
  const texts = { open: '#1e40af', in_progress: '#92400e', resolved: '#065f46', closed: '#6b7280' };
  const bg = bgs[status] || bgs.open;
  const text = texts[status] || texts.open;
  const Icon = icons[status] || AlertCircle;
  const label = t?.statusMap?.[status] || status;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: bg, color: text }}>
      <Icon size={10} /> {label}
    </span>
  );
};

const formatDate = (iso, locale = 'pt-BR') => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(locale) + ' ' + d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

// ─── New Ticket Modal ─────────────────────────────────────────────────────────

const NewTicketModal = ({ open, onClose, clubId, onCreated, t }) => {
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general' });
  const [submitting, setSubmitting] = useState(false);

  const categories = t.categories;
  const priorities = t.priorities;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return;
    setSubmitting(true);
    try {
      await AZURE.post('/support/tickets', { institution_id: clubId, ...form });
      onCreated?.();
      onClose();
      setForm({ subject: '', description: '', priority: 'medium', category: 'general' });
    } catch (err) {
      console.error('Erro ao criar ticket:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: VUKZ.border }}>
          <h3 className="font-bold" style={{ color: VUKZ.text }}>{t.modalTitle}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">{t.subject}</label>
            <input
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: VUKZ.border }}
              placeholder={t.subjectPlaceholder}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t.category}</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: VUKZ.border }}
              >
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t.priority}</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: VUKZ.border }}
              >
                {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">{t.description}</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm h-28 resize-none"
              style={{ borderColor: VUKZ.border }}
              placeholder={t.descriptionPlaceholder}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: VUKZ.primary }}
            >
              {submitting ? t.creating : t.createBtn}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Ticket Detail Panel ──────────────────────────────────────────────────────

const TicketDetail = ({ ticket, onClose, onReply, t, locale }) => {
  const [reply, setReply] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!ticket) return;
    const loadMessages = async () => {
      setLoadingMsgs(true);
      try {
        const res = await AZURE.get(`/support/tickets/${ticket.ticket_id}/messages`);
        setMessages(res.data?.data || res.data || []);
      } catch (e) {
        console.error('Error loading messages:', e);
      } finally {
        setLoadingMsgs(false);
      }
    };
    loadMessages();
  }, [ticket]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await AZURE.post(`/support/tickets/${ticket.ticket_id}/messages`, { body: reply });
      setMessages(prev => [...prev, { body: reply, sender_type: 'user', created_at: new Date().toISOString() }]);
      setReply('');
      onReply?.();
    } catch (e) {
      console.error('Error sending reply:', e);
    } finally {
      setSending(false);
    }
  };

  if (!ticket) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: VUKZ.border }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <PriorityDot priority={ticket.priority} />
              <StatusBadge status={ticket.status} />
            </div>
            <h3 className="font-bold text-sm truncate" style={{ color: VUKZ.text }}>{ticket.subject}</h3>
            <p className="text-[10px] text-gray-400">#{ticket.ticket_id} • {formatDate(ticket.created_at, locale)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2"><X size={18} /></button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Original description */}
          {ticket.description && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1 font-semibold">{t.originalDesc}</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          )}

          {loadingMsgs ? (
            <div className="space-y-2">
              {[1, 2].map(i => <div key={i} className="h-16 rounded-lg animate-pulse bg-gray-100" />)}
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-4">{t.noMessages}</p>
          ) : (
            messages.map((msg, i) => (
              <div
                key={i}
                className={`rounded-lg p-3 text-sm ${msg.sender_type === 'user' ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}
              >
                <p className="text-[10px] text-gray-400 mb-1">
                  {msg.sender_type === 'user' ? t.you : t.support} • {formatDate(msg.created_at, locale)}
                </p>
                <p className="text-gray-700 whitespace-pre-wrap">{msg.body}</p>
              </div>
            ))
          )}
        </div>

        {/* Reply input */}
        {ticket.status !== 'closed' && (
          <div className="p-3 border-t flex gap-2" style={{ borderColor: VUKZ.border }}>
            <input
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={t.replyPlaceholder}
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: VUKZ.border }}
            />
            <button
              onClick={handleSend}
              disabled={sending || !reply.trim()}
              className="p-2 rounded-lg text-white disabled:opacity-40"
              style={{ backgroundColor: VUKZ.primary }}
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Support Page — Chamados & Atendimento
// ═══════════════════════════════════════════════════════════════════════════════

export default function SupportPage() {
  const { clubId: ctxClubId } = useActiveContext();
  const { clubId: rbacClubId } = useRBAC();
  const clubId = ctxClubId || rbacClubId;
  const { user } = useAuth();
  const { lang } = useGovernanceLang();
  const t = SUPPORT_LABELS[lang] || SUPPORT_LABELS.pt;
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all'); // all, open, resolved

  useEffect(() => {
    if (!clubId) return;
    loadTickets();
  }, [clubId]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await AZURE.get(`/support/tickets?institution_id=${clubId}`);
      setTickets(res.data?.data || res.data || []);
    } catch (e) {
      console.error('Error loading tickets:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = tickets.filter(t => {
    if (filter === 'open') return t.status === 'open' || t.status === 'in_progress';
    if (filter === 'resolved') return t.status === 'resolved' || t.status === 'closed';
    return true;
  });

  const stats = {
    open: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length,
    total: tickets.length,
  };

  if (!clubId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: VUKZ.bg }}>
        <p className="text-gray-500">{t.noClub}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <PageHeader
        title={t.pageTitle}
        subtitle={t.pageSubtitle}
        icon={LifeBuoy}
        action={
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: VUKZ.primary }}
          >
            <Plus size={14} /> {t.newTicketBtn}
          </button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 pb-8 space-y-5">
        {/* ─── Stats Row ─────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: VUKZ.border }}>
            <p className="text-xl font-bold" style={{ color: VUKZ.text }}>{stats.total}</p>
            <p className="text-[10px] text-gray-400">{t.statsTotal}</p>
          </div>
          <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: VUKZ.border }}>
            <p className="text-xl font-bold text-amber-600">{stats.open}</p>
            <p className="text-[10px] text-gray-400">{t.statsOpen}</p>
          </div>
          <div className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: VUKZ.border }}>
            <p className="text-xl font-bold text-green-600">{stats.resolved}</p>
            <p className="text-[10px] text-gray-400">{t.statsResolved}</p>
          </div>
        </div>

        {/* ─── Filter Tabs ───────────────────────────────────────── */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { key: 'all', label: t.filterAll },
            { key: 'open', label: t.filterOpen },
            { key: 'resolved', label: t.filterResolved },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                filter === tab.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ─── Tickets List ──────────────────────────────────────── */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 rounded-lg animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center">
              <LifeBuoy size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">{t.noTickets}</p>
              <button
                onClick={() => setShowNew(true)}
                className="mt-3 text-sm font-semibold hover:underline"
                style={{ color: VUKZ.primary }}
              >
                {t.firstTicket}
              </button>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: VUKZ.border }}>
              {filtered.map(ticket => (
                <button
                  key={ticket.ticket_id}
                  onClick={() => setSelectedTicket(ticket)}
                  className="w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <PriorityDot priority={ticket.priority} />
                  <div className="flex-1 min-w-0 ml-3">
                    <p className="text-sm font-medium truncate" style={{ color: VUKZ.text }}>{ticket.subject}</p>
                    <p className="text-[10px] text-gray-400">
                      #{ticket.ticket_id} • {formatDate(ticket.created_at, locale)}
                      {ticket.category && ` • ${ticket.category}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <StatusBadge status={ticket.status} t={t} />
                    <ChevronRight size={14} className="text-gray-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Modals ──────────────────────────────────────────────── */}
      <NewTicketModal open={showNew} onClose={() => setShowNew(false)} clubId={clubId} onCreated={loadTickets} t={t} />
      {selectedTicket && (
        <TicketDetail ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onReply={loadTickets} t={t} locale={locale} />
      )}
    </div>
  );
}
